import { RequestFailureKind } from '@amzn/aac-chrome-internal-api';
import { logger } from 'src/commons/utils/logger';
import { AjaxRequestPublisher } from 'src/commons/utils/metrics';
import {
  NETWORK_ERROR_REGEX,
  REQUEST_ERROR_REGEX,
  USER_ABORTED_REQUEST_ERROR,
} from './constants';

/*
 * The counter suffix each classified failure gets, on top of the `:success` 0
 * every failure already publishes. Every kind is counted, so a failure-rate
 * change on an endpoint can be attributed from the metrics alone.
 *
 * `timeout` is a NEW series. Before fetchWithTimeout classified failures, this
 * module's own timeout surfaced as a DOMException whose message matched
 * USER_ABORTED_REQUEST_ERROR, so it was counted as `:userAbortedRequest` --
 * a name that says the user gave up when in fact the client's own deadline
 * fired. Caller cancellations never reach here at all (fetchWithTimeout
 * reports nothing for them), so `:userAbortedRequest` was only ever timeouts.
 *
 * `http` is split by status class in httpSuffix below. fetchWithTimeout
 * classifies a redirect and a 403 before it reaches `http`, so `:http4xx` is
 * every 4xx except 403, which counts as `:forbidden`. A failure rate across
 * all kinds has to sum every suffix. The `httpFailure` entry is a defensive
 * fallback for an `http` failure without a 4xx/5xx status, which
 * fetchWithTimeout does not produce today.
 */
const KIND_METRIC_SUFFIX: Record<RequestFailureKind, string> = {
  network: 'networkFailure',
  timeout: 'timeout',
  redirect: 'redirect',
  forbidden: 'forbidden',
  http: 'httpFailure',
  parse: 'parseFailure',
  unknown: 'unknownFailure',
};

/*
 * The status CLASS, not the raw status: one series per class keeps the metric
 * count bounded while still separating a client error from a server error.
 */
const httpSuffix = (status?: number): string => {
  if (status !== undefined && status >= 500 && status <= 599) {
    return 'http5xx';
  }
  if (status !== undefined && status >= 400 && status <= 499) {
    return 'http4xx';
  }
  return KIND_METRIC_SUFFIX.http;
};

const kindSuffix = (kind: RequestFailureKind, status?: number): string =>
  kind === 'http' ? httpSuffix(status) : KIND_METRIC_SUFFIX[kind];

type HandleErrorProps = {
  // `unknown`, not `Error`: fetch-request forwards whatever `fetch` rejected
  // with, so a non-Error value has to be handled rather than assumed away.
  error: unknown;
  loggerMessage: string;
  metricSlug: string;
  /*
   * fetchWithTimeout's classification. Absent when the failure came from a
   * caller that does not classify -- fetchRequest, or a page running an OLDER
   * global window.AACChrome.InternalAPI whose fetchWithTimeout predates
   * `kind` -- in which case the error-message regexes below still decide.
   */
  kind?: RequestFailureKind;
  // The HTTP status, when the failure came with a response.
  status?: number;
};

const logError = ({
  error,
  loggerMessage,
  metricSlug,
  kind,
  status,
}: HandleErrorProps) => {
  const context = {
    ...(kind !== undefined ? { kind } : {}),
    ...(status !== undefined ? { status } : {}),
  };
  const hasContext = Object.keys(context).length > 0;

  // Pass the Error itself so KatalLogger records its name, message and stack.
  // Stringifying a wrapper object here used to log the literal
  // "[object Object]" and discard all three. `kind` and `status` ride in the
  // THIRD argument -- KatalLogger's (message, Error, context) overload keeps
  // the error fields and merges the context into the same record, so neither
  // has to be given up for the other.
  if (error instanceof Error) {
    if (hasContext) {
      logger.error(loggerMessage, error, context);
    } else {
      logger.error(loggerMessage, error);
    }
  } else {
    // KatalLogger spreads a non-Error second argument into the record's
    // context. Wrapping the value in `new Error()` would invent a stack
    // pointing here.
    logger.error(loggerMessage, { error: String(error), ...context });
  }

  if (kind !== undefined) {
    AjaxRequestPublisher.publishCounterMonitor(
      `${metricSlug}:${kindSuffix(kind, status)}`,
      1,
    );
    return;
  }

  const errorMessage = error instanceof Error ? error.message : String(error);
  if (NETWORK_ERROR_REGEX.test(errorMessage)) {
    AjaxRequestPublisher.publishCounterMonitor(
      `${metricSlug}:networkFailure`,
      1,
    );
  } else if (REQUEST_ERROR_REGEX.test(errorMessage)) {
    AjaxRequestPublisher.publishCounterMonitor(
      `${metricSlug}:requestAborted`,
      1,
    );
  } else if (USER_ABORTED_REQUEST_ERROR.test(errorMessage)) {
    AjaxRequestPublisher.publishCounterMonitor(
      `${metricSlug}:userAbortedRequest`,
      1,
    );
  }
};

export default logError;
